import { parseISO, type Duration } from 'date-fns';
import { add, endOfDay } from 'date-fns/fp';
import { formatRFC3339 } from '#util/time.ts';

export const expirationOptions = [
  { value: 'never', label: 'No expiration' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '365d', label: '365 days' },
  { value: 'custom', label: 'Custom…' },
] as const;

export type ExpirationPreset = (typeof expirationOptions)[number]['value'];

export const DEFAULT_EXPIRATION: ExpirationPreset = '30d';

const presetDurations: Record<Exclude<ExpirationPreset, 'never' | 'custom'>, Duration> = {
  '7d': { days: 7 },
  '30d': { days: 30 },
  '90d': { days: 90 },
  '365d': { days: 365 },
};

/**
 * Convert the form's expiration selection into the RFC3339 `expiresAt` the API expects.
 * A custom date expires at the end of that day (local time), so picking today's date
 * yields a key that works for the rest of the day.
 */
export function buildExpiresAt(
  expiration: ExpirationPreset,
  customDate: string,
  now: Date | number = Date.now()
): string | undefined {
  if (expiration === 'never') {
    return undefined;
  }
  if (expiration === 'custom') {
    return formatRFC3339(endOfDay(parseISO(customDate)));
  }
  return formatRFC3339(add(presetDurations[expiration])(now));
}
